import React, { useState, useEffect, useMemo } from "react";
import { Card } from "@/components/ui/card";
import moment from "moment";
import Layout2 from "@/components/Layout/Layout2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { getRoadmap } from "@/api/new-api/staff-management/time-sheet/time-sheet";
import { toast } from "react-hot-toast";
import { User, Calendar as CalendarIcon, Clock, Coffee, CheckCircle2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import Loading from "@/components/common/loading";

const TimeSchedulerComponent = () => {
  const { id: paramId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Fallback to location state id or query param if available
  const id = paramId || new URLSearchParams(location.search).get("id") || location.state?.id || location.state?.item?.id;

  const [loading, setLoading] = useState(true);
  const [roadmapData, setRoadmapData] = useState(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    const fetchRoadmapData = async () => {
      setLoading(true);
      try {
        const res = await getRoadmap(id);
        if (res?.status === "success" && res?.data) {
          setRoadmapData(res.data);
        } else {
          toast.error(res?.message || "Failed to load attendance details");
        }
      } catch (error) {
        console.error("Error fetching roadmap data:", error);
        toast.error("Failed to load attendance details");
      } finally {
        setLoading(false);
      }
    };

    fetchRoadmapData();
  }, [id]);

  const times = useMemo(
    () =>
      Array.from(
        { length: 48 },
        (_, i) => `${String(i % 24).padStart(2, "0")}:00`
      ),
    []
  );

  const getPosition = (time) => {
    if (!time) return 0;
    const parts = time.split(":").map(Number);
    const hour = parts[0] || 0;
    const minute = parts[1] || 0;
    const second = parts[2] || 0;
    return hour * 80 + (minute / 60) * 80 + (second / 3600) * 80;
  };

  const getWidth = (start, end) => {
    if (!start || !end) return 80;
    const startPos = getPosition(start);
    let endPos = getPosition(end);
    if (endPos <= startPos) endPos += 24 * 80;
    return Math.max(endPos - startPos, 28);
  };

  const calculateDurationMinutes = (start, end) => {
    if (!start || !end) return 0;
    const s = moment(start, ["HH:mm:ss", "HH:mm"]);
    const e = moment(end, ["HH:mm:ss", "HH:mm"]);
    let diff = e.diff(s, "minutes");
    if (diff < 0) diff += 24 * 60;
    return diff > 0 ? diff : 0;
  };

  const formatMinutes = (totalMinutes) => {
    if (!totalMinutes || totalMinutes <= 0) return "0 mins";
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (hours === 0) return `${mins} mins`;
    if (mins === 0) return `${hours} hrs`;
    return `${hours} hrs ${mins} mins`;
  };

  const attendanceDate = roadmapData?.attendance_date
    ? moment(roadmapData.attendance_date, "YYYY-MM-DD")
    : moment();
  const nextDay = moment(attendanceDate).add(1, "day");

  // Calculate Check-In and Break Events
  const checkInEvents = useMemo(() => {
    const data = roadmapData?.check_in_data || [];
    return data.map((shift, index) => ({
      id: `check-in-${index}`,
      type: "work",
      start: shift.start_time,
      end: shift.end_time,
      row: index + 1,
      label: data.length > 1 ? `Check-in ${index + 1}` : "Worked Shift",
      color: "#22C55E",
      badgeClass: "bg-emerald-500 text-black border border-emerald-400 font-semibold shadow-md",
    }));
  }, [roadmapData?.check_in_data]);

  const breakEvents = useMemo(() => {
    const data = roadmapData?.break_data || [];
    const baseRow = checkInEvents.length > 0 ? checkInEvents.length + 1 : 2;
    return data.map((brk, index) => ({
      id: `break-${index}`,
      type: "break",
      start: brk.start_time,
      end: brk.end_time,
      row: baseRow + index,
      label: `Break ${index + 1}`,
      color: "#F59E0B",
      badgeClass: "bg-amber-500 text-black border border-amber-400 font-semibold shadow-md",
    }));
  }, [roadmapData?.break_data, checkInEvents]);

  const allEvents = useMemo(() => [...checkInEvents, ...breakEvents], [checkInEvents, breakEvents]);

  // Statistics
  const totalCheckInMinutes = useMemo(() => {
    return (roadmapData?.check_in_data || []).reduce(
      (acc, curr) => acc + calculateDurationMinutes(curr.start_time, curr.end_time),
      0
    );
  }, [roadmapData?.check_in_data]);

  const totalBreakMinutes = useMemo(() => {
    return (roadmapData?.break_data || []).reduce(
      (acc, curr) => acc + calculateDurationMinutes(curr.start_time, curr.end_time),
      0
    );
  }, [roadmapData?.break_data]);

  const netWorkedMinutes = Math.max(0, totalCheckInMinutes - totalBreakMinutes);

  const totalRows = Math.max(9, allEvents.length + 1);

  if (loading) {
    return <Loading links={backofficeLinks} />;
  }

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
      pageName={{
        title: roadmapData?.staff_name
          ? `${roadmapData.staff_name} - Time Schedule`
          : "Time Schedule",
      }}
      paths={[
        { name: "BackOffice", path: "/backoffice" },
        {
          name: "Staff Management",
          path: "/backoffice/staff-management/staff/all",
        },
        { name: "Time Sheet", path: "/backoffice/staff-management/time-sheet" },
        { name: "Time Schedule" },
      ]}
    >
      <div className="container min-h-screen text-white p-4 md:p-6 space-y-6">
        {/* Top Header Controls */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-cblack4 p-4 rounded-xl border border-neutral-800">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <User className="w-5 h-5 text-cyellow" />
                {roadmapData?.staff_name || "Staff Attendance Details"}
              </h1>
              <p className="text-xs text-neutral-400 flex items-center gap-1 mt-0.5">
                <CalendarIcon className="w-3.5 h-3.5" />
                {attendanceDate.format("dddd, DD MMMM YYYY")}
              </p>
            </div>
          </div>

          {/* Quick Metrics */}
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <div className="bg-cblack3 border border-neutral-700 px-3.5 py-1.5 rounded-lg flex items-center gap-2">
              <Clock className="w-4 h-4 text-emerald-400" />
              <div>
                <span className="text-[10px] text-neutral-400 block uppercase leading-none">
                  Check-in Time
                </span>
                <span className="text-xs font-semibold text-emerald-400">
                  {formatMinutes(totalCheckInMinutes)}
                </span>
              </div>
            </div>

            <div className="bg-cblack3 border border-neutral-700 px-3.5 py-1.5 rounded-lg flex items-center gap-2">
              <Coffee className="w-4 h-4 text-amber-400" />
              <div>
                <span className="text-[10px] text-neutral-400 block uppercase leading-none">
                  Break Time
                </span>
                <span className="text-xs font-semibold text-amber-400">
                  {formatMinutes(totalBreakMinutes)}
                </span>
              </div>
            </div>

            <div className="bg-cblack3 border border-neutral-700 px-3.5 py-1.5 rounded-lg flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-cyellow" />
              <div>
                <span className="text-[10px] text-neutral-400 block uppercase leading-none">
                  Net Worked
                </span>
                <span className="text-xs font-semibold text-cyellow">
                  {formatMinutes(netWorkedMinutes)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-between flex-wrap gap-4 text-xs text-neutral-300 px-2">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="w-3.5 h-3.5 rounded-full bg-emerald-500 inline-block shadow-sm"></span>
              <span className="font-medium text-neutral-300">Worked / Check-In</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3.5 h-3.5 rounded-full bg-amber-500 inline-block shadow-sm"></span>
              <span className="font-medium text-neutral-300">Break Time</span>
            </div>
          </div>

          <div className="flex items-center gap-6 text-neutral-400 font-medium">
            <span>Day 1: {attendanceDate.format("ddd, DD MMM")}</span>
            <span>Day 2: {nextDay.format("ddd, DD MMM")}</span>
          </div>
        </div>

        {/* Schedule Timeline Chart */}
        <div className="overflow-x-auto rounded-xl border border-neutral-800 bg-[#121212] shadow-xl custom-scrollbar">
          <div className="flex min-w-[3840px] h-[65vh] relative">
            {/* Left GMT & Row Header */}
            <div className="w-20 flex flex-col items-center text-neutral-400 text-xs border-r border-neutral-800 bg-[#181818] sticky left-0 z-20 shadow-md">
              <div className="h-10 flex items-center justify-center font-semibold text-neutral-300 border-b border-neutral-800 w-full text-[11px]">
                Timeline
              </div>
              {Array.from({ length: totalRows }).map((_, i) => (
                <div
                  key={i}
                  className="h-16 flex items-center justify-center text-neutral-500 border-b border-neutral-800/80 w-full font-mono text-[11px]"
                >
                  Row {i + 1}
                </div>
              ))}
            </div>

            {/* Timeline Matrix */}
            <div className="relative flex-1 bg-[#121212]">
              {/* Sticky Top Hour Markers */}
              <div className="flex sticky top-0 z-10 bg-[#181818] border-b border-neutral-800 shadow-sm">
                {times.map((time, i) => (
                  <div
                    key={i}
                    className={`w-20 text-center text-xs text-neutral-300 py-2.5 font-mono border-r border-neutral-800 ${i === 24 ? "border-l-2 border-cyellow text-cyellow font-semibold" : ""
                      }`}
                  >
                    {time}
                  </div>
                ))}
              </div>

              {/* Grid Background */}
              <div className="absolute top-10 left-0 w-[3840px] h-full pointer-events-none">
                {Array.from({ length: 48 }).map((_, colIndex) =>
                  Array.from({ length: totalRows }).map((_, rowIndex) => (
                    <div
                      key={`${rowIndex}-${colIndex}`}
                      className={`absolute border-r border-b border-neutral-800/50 w-20 h-16 box-border ${colIndex === 24 ? "border-l-2 border-neutral-700" : ""
                        }`}
                      style={{
                        top: `${rowIndex * 64}px`,
                        left: `${colIndex * 80}px`,
                      }}
                    ></div>
                  ))
                )}
              </div>

              {/* Event Ranges */}
              <div className="absolute top-10 left-0 w-[3840px] h-full pointer-events-none">
                {allEvents.map((event) => {
                  const startX = getPosition(event.start);
                  const width = getWidth(event.start, event.end);
                  const isSmall = width < 140;

                  return (
                    <div
                      key={event.id}
                      className={`absolute rounded-full px-2 py-1 text-xs font-semibold flex items-center justify-center pointer-events-auto transition-transform hover:scale-[1.02] shadow-lg cursor-pointer ${event.badgeClass}`}
                      style={{
                        top: `${(event.row - 1) * 64 + 18}px`,
                        left: `${startX}px`,
                        width: `${width}px`,
                        minHeight: "28px",
                      }}
                      title={`${event.label}: ${event.start} - ${event.end}`}
                    >
                      {isSmall ? (
                        <>
                          {/* Indicator dot inside small range */}
                          <span className="w-2 h-2 rounded-full bg-black/60 shrink-0"></span>

                          {/* Persistent floating tooltip callout above small range */}
                          <div className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-[#1a1a1a] text-white text-[11px] font-medium px-2.5 py-1 rounded-md shadow-2xl border border-neutral-700 pointer-events-none z-30 flex items-center gap-1.5">
                            <span
                              className={
                                event.type === "break"
                                  ? "text-amber-400 font-semibold"
                                  : "text-emerald-400 font-semibold"
                              }
                            >
                              {event.label}:
                            </span>
                            <span className="font-mono text-neutral-200">
                              {event.start} - {event.end}
                            </span>
                            {/* Triangle pointer */}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1a1a1a]"></div>
                          </div>
                        </>
                      ) : (
                        <span className="truncate px-1.5">
                          {event.label}: {event.start} - {event.end}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout2>
  );
};

export default TimeSchedulerComponent;
